import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ButtonLoader } from "Components";
import { unAuthService } from "Service/Axios";
import { SAVE_PASSWORD_URL } from "Constant/api";
import { Validator } from "Utils/validator";
import { paths } from "Constant/paths";
import { passwordRules } from "Constant/constants";
import { checkPasswordStrength, encryptFun } from "Utils";

export default function ResetPassword() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [passwordVisiblity, setPasswordVisiblity] = useState(false);
  const [confirmPasswordVisiblity, setConfirmPasswordVisiblity] =
    useState(false);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errMsg, setErrMsg] = useState({
    password: "",
    confirmPassword: "",
  });
  const [passwordStrength, setPasswordStrength] = useState({
    label: "",
    value: 0,
    errors: [],
  });

  const { password, confirmPassword } = formData;
  const passwordLength = password.length > 0;
  const { firstName, lastName, email } = location.state || {};

  const togglePassword = () => {
    setPasswordVisiblity(!passwordVisiblity);
  };
  const toggleConfirmPassword = () => {
    setConfirmPasswordVisiblity(!confirmPasswordVisiblity);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    if (e.target.name === "password") {
      setPasswordStrength(
        checkPasswordStrength(e.target.value, firstName, lastName, email)
      );
      if (!e.target.value) {
        setErrMsg((prev) => ({
          ...prev,
          password: t("common.messages.error.common.password.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          password: "",
        }));
      }
    }

    if (e.target.name === "confirmPassword") {
      if (!e.target.value) {
        setErrMsg((prev) => ({
          ...prev,
          confirmPassword: t(
            "common.messages.error.common.confirmPassword.blank"
          ),
        }));
      } else if (
        e.target.value &&
        !Validator.confirmPasswordCheck(password, e.target.value)
      ) {
        setErrMsg((prev) => ({
          ...prev,
          confirmPassword: t(
            "common.messages.error.common.confirmPassword.inValid"
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          confirmPassword: "",
        }));
      }
    }
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      const passwordStrengthResult = checkPasswordStrength(
        password,
        firstName,
        lastName,
        email
      );
      if (
        password &&
        passwordStrengthResult.value === 100 &&
        confirmPassword &&
        Validator.confirmPasswordCheck(password, confirmPassword)
      ) {
        resolve(true);
      } else {
        if (!password) {
          setErrMsg((prev) => ({
            ...prev,
            password: t("common.messages.error.common.password.blank"),
          }));
        }

        if (!confirmPassword) {
          setErrMsg((prev) => ({
            ...prev,
            confirmPassword: t(
              "common.messages.error.common.confirmPassword.blank"
            ),
          }));
        } else if (
          confirmPassword &&
          !Validator.confirmPasswordCheck(password, confirmPassword)
        ) {
          setErrMsg((prev) => ({
            ...prev,
            confirmPassword: t(
              "common.messages.error.common.confirmPassword.inValid"
            ),
          }));
        }

        resolve(false);
      }
    });
  };

  const onResetPassword = async (e) => {
    e.preventDefault();
    const validationStatus = await getValidation();
    if (validationStatus) {
      setLoading(true);
      const encryptedPassword = encryptFun(password);
      try {
        const payload = {
          username: email,
          email: email,
          password: `${encryptedPassword}`,
        };
        const response = await unAuthService(
          SAVE_PASSWORD_URL,
          "POST",
          payload
        );
        setLoading(false);
        if (response && response.success) {
          if (
            response.message === "The new password has been saved successfully"
          ) {
            toast.success(
              t("common.messages.notification.success.passwordReset")
            );
            navigate(paths.login);
          } else {
            toast.error(response.message);
          }
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        setLoading(false);
      }
    }
  };

  if (!location.state || !email || !firstName || !lastName) {
    return <Navigate to={paths.forgetPassword} />;
  }

  return (
    <div className="px-lg-4 d-flex flex-column justify-content-center max-w-400 mx-auto login-box w-100">
      <div className="mb-5">
        <h3 className="text-xl-4xl text-2xl font-semibold">
          {t("common.button.resetPassword")}
        </h3>
        <div className="text-sm text-grey-600">
          <span>{t("guest.resetPassword.info")}</span>
        </div>
      </div>

      <div className="new-onbording-form">
        <div className="mb-4">
          <label htmlFor="password" className="form-label mb-1">
            {t("common.label.newPassword")}
          </label>
          <div className="position-relative show-hide-pw">
            <input
              type={passwordVisiblity ? "text" : "password"}
              className="form-control form-control-lg pr-10 rounded-lg"
              id="password"
              // placeholder={t("common.placeHolder.password")}
              placeholder="Enter Password"
              name="password"
              value={password}
              onChange={handleChange}
            />
            <button
              className="border-start-0 position-absolute top-0 end-0 bottom-0 w-10 d-flex justify-content-center align-items-center text-grey-300 border-0 bg-transparent z-5"
              onClick={togglePassword}
            >
              <i className="bx bx-show-alt"></i>
              <i className="bx bx-hide"></i>
            </button>
          </div>
          {errMsg.password && <p className="error">{errMsg.password}</p>}

          <ul className="list-unstyled text-sm mt-3">
            <li
              className={`mb-2 d-flex align-items-center ${
                passwordStrength.label === "Strong"
                  ? "text-success"
                  : passwordStrength.label === "Medium"
                  ? "text-warning"
                  : passwordStrength.label === "Weak"
                  ? "text-danger"
                  : ""
              }`}
            >
              <i className="bx bx-check bx-sm check-icon me-1"></i>
              <span className="status-text">
                Password Strength :{" "}
                <span className="strength-status">
                  {passwordStrength.label || "Weak"}
                </span>
              </span>
            </li>

            {passwordRules.map((rule, idx) => {
              const isFailed = passwordStrength.errors.includes(rule);
              return (
                <li
                  key={idx}
                  className={`mb-2 d-flex align-items-center ${
                    passwordLength
                      ? isFailed
                        ? "not-matched-text"
                        : "matched-text"
                      : ""
                  }`}
                >
                  <i
                    className={`bx bx-sm check-icon me-1 ${
                      isFailed ? "bx-x" : "bx-check"
                    }`}
                  ></i>
                  <span className="status-text">{rule}</span>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="mb-4">
          <label htmlFor="confirmPassword" className="form-label mb-1">
            {t("common.label.confirmPassword")}
          </label>
          <div className="confirmPassword position-relative show-hide-pw">
            <input
              type={confirmPasswordVisiblity ? "text" : "password"}
              className="form-control form-control-lg pr-10 rounded-lg"
              id="confirmPassword"
              // placeholder={t("common.placeHolder.confirmPassword")}
              placeholder="Enter Password"
              name="confirmPassword"
              value={confirmPassword}
              onChange={handleChange}
            />
            <button
              className="border-start-0 position-absolute top-0 end-0 bottom-0 w-10 d-flex justify-content-center align-items-center text-grey-300 border-0 bg-transparent z-5"
              onClick={toggleConfirmPassword}
            >
              <i className="bx bx-show-alt"></i>
              <i className="bx bx-hide"></i>
            </button>
          </div>
          {errMsg.confirmPassword && (
            <p className="error">{errMsg.confirmPassword}</p>
          )}
        </div>
        <div className="d-grid">
          {!loading ? (
            <button
              type="submit"
              className="btn btn-primary btn-lg rounded-lg"
              onClick={onResetPassword}
            >
              {t("common.button.resetPassword")}
            </button>
          ) : (
            <ButtonLoader />
          )}
        </div>
      </div>
    </div>
  );
}
